import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef, AfterViewInit, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonIcon } from '@ionic/angular/standalone';

export interface BubbleItem {
  id: string;
  icon: string;
  label: string;
  order: number;
  disabled?: boolean;
}

@Component({
  selector: 'app-bubble-bar',
  imports: [CommonModule, IonIcon],
  templateUrl: './bubble-bar.component.html',
  styleUrls: ['./bubble-bar.component.scss']
})
export class BubbleBarComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() items: BubbleItem[] = [];
  @Input() activeItemId: string | null = null;
  @Input() enableReorder = true;
  @Input() enableHaptics = true;

  @Output() itemClick = new EventEmitter<string>();
  @Output() itemsReordered = new EventEmitter<BubbleItem[]>();

  @ViewChild('bubbleContainer', { read: ElementRef }) bubbleContainer!: ElementRef;

  sortedItems: BubbleItem[] = [];
  selectorPosition = 0; // X position of selector
  isDragging = false;
  dragStartX = 0;
  dragStartPosition = 0;
  itemPositions: Map<string, number> = new Map();

  private readonly ITEM_WIDTH = 28 + 6; // bubble width + gap
  private readonly BUBBLE_WIDTH = 40;

  ngOnInit() {
    this.sortedItems = [...this.items].sort((a, b) => a.order - b.order);
    this.updateSelectorPosition();
  }

  ngAfterViewInit() {
    // Use setTimeout to ensure DOM is fully rendered
    setTimeout(() => {
      this.calculateItemPositions();
      this.updateSelectorPosition();
    }, 0);
  }

  ngOnChanges() {
    // Update sorted items when items change
    this.sortedItems = [...this.items].sort((a, b) => a.order - b.order);

    setTimeout(() => {
      this.calculateItemPositions();
      this.updateSelectorPosition();
    }, 0);
  }

  private calculateItemPositions() {
    if (!this.bubbleContainer) return;

    const container = this.bubbleContainer.nativeElement;
    const bubbles = container.querySelectorAll('.bubble-item');

    this.itemPositions.clear();
    bubbles.forEach((bubble: Element, index: number) => {
      const rect = bubble.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const centerX = rect.left - containerRect.left + rect.width / 2;
      const item = this.sortedItems[index];
      if (item) {
        this.itemPositions.set(item.id, centerX - this.BUBBLE_WIDTH / 2);
      }
    });
  }

  private updateSelectorPosition() {
    if (!this.activeItemId) {
      this.selectorPosition = 0;
      return;
    }

    const activeIndex = this.sortedItems.findIndex(item => item.id === this.activeItemId);
    if (activeIndex >= 0) {
      // Calculate position based on index
      const position = this.itemPositions.get(this.activeItemId);
      if (position !== undefined) {
        this.selectorPosition = position;
      } else {
        // Fallback calculation
        this.selectorPosition = activeIndex * this.ITEM_WIDTH;
      }
    }
  }

  getSelectorTransform(): string {
    return `translate(${this.selectorPosition}px, -50%)`;
  }

  isUnderSelector(itemId: string): boolean {
    // Check if item is under the selector (for changing icon color)
    const itemPos = this.itemPositions.get(itemId);
    if (itemPos === undefined) return this.activeItemId === itemId;

    const selectorCenter = this.selectorPosition + this.BUBBLE_WIDTH / 2;
    const itemCenter = itemPos + this.BUBBLE_WIDTH / 2;

    return Math.abs(selectorCenter - itemCenter) < this.BUBBLE_WIDTH / 2;
  }

  onSelectorMouseDown(event: MouseEvent) {
    this.isDragging = false; // Will be set to true on move
    this.dragStartX = event.clientX;
    this.dragStartPosition = this.selectorPosition;
  }

  onSelectorTouchStart(event: TouchEvent) {
    event.preventDefault();
    const touch = event.touches[0];
    this.isDragging = false; // Will be set to true on move
    this.dragStartX = touch.clientX;
    this.dragStartPosition = this.selectorPosition;
  }

  onMouseMove(event: MouseEvent) {
    if (this.dragStartX === 0) return;

    const deltaX = event.clientX - this.dragStartX;

    // Start dragging if moved more than 5px
    if (!this.isDragging && Math.abs(deltaX) > 5) {
      this.isDragging = true;
      this.triggerHaptic('medium');
    }

    if (!this.isDragging) return;
    event.preventDefault();

    this.selectorPosition = this.dragStartPosition + deltaX;

    // Clamp to container bounds (free movement)
    const containerWidth = this.bubbleContainer?.nativeElement.clientWidth || 0;
    const maxPosition = containerWidth - this.BUBBLE_WIDTH - 16; // padding
    this.selectorPosition = Math.max(0, Math.min(maxPosition, this.selectorPosition));
  }

  onTouchMove(event: TouchEvent) {
    if (this.dragStartX === 0) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - this.dragStartX;

    // Start dragging if moved more than 5px
    if (!this.isDragging && Math.abs(deltaX) > 5) {
      this.isDragging = true;
      this.triggerHaptic('medium');
    }

    if (!this.isDragging) return;
    event.preventDefault();

    this.selectorPosition = this.dragStartPosition + deltaX;

    // Clamp to container bounds (free movement)
    const containerWidth = this.bubbleContainer?.nativeElement.clientWidth || 0;
    const maxPosition = containerWidth - this.BUBBLE_WIDTH - 16; // padding
    this.selectorPosition = Math.max(0, Math.min(maxPosition, this.selectorPosition));
  }

  onMouseUp(event: MouseEvent) {
    if (this.isDragging) {
      this.snapToNearestItem();
    } else if (this.dragStartX !== 0) {
      // It's a click, find item under cursor
      const itemUnder = this.getItemAtPosition(event.clientX);
      if (itemUnder) {
        this.onItemClick(itemUnder.id);
      }
    }
    this.dragStartX = 0;
  }

  onTouchEnd(event: TouchEvent) {
    if (this.isDragging) {
      this.snapToNearestItem();
    } else if (this.dragStartX !== 0) {
      // It's a tap, find item under finger
      const touch = event.changedTouches[0];
      const itemUnder = this.getItemAtPosition(touch.clientX);
      if (itemUnder) {
        this.onItemClick(itemUnder.id);
      }
    }
    this.dragStartX = 0;
  }

  private snapToNearestItem() {
    // Find nearest item based on actual position
    const selectorCenter = this.selectorPosition + this.BUBBLE_WIDTH / 2;

    let nearestItemId: string | null = null;
    let minDistance = Infinity;

    this.itemPositions.forEach((pos, itemId) => {
      const item = this.sortedItems.find(i => i.id === itemId);
      if (!item || item.disabled) return;

      const itemCenter = pos + this.BUBBLE_WIDTH / 2;
      const distance = Math.abs(selectorCenter - itemCenter);

      if (distance < minDistance) {
        minDistance = distance;
        nearestItemId = item.id;
      }
    });

    // Stop dragging first to enable smooth transition
    this.isDragging = false;
    this.triggerHaptic('light');

    if (nearestItemId) {
      // Force recentrer le sélecteur sur l'item le plus proche (même si c'est déjà actif)
      const position = this.itemPositions.get(nearestItemId);
      if (position !== undefined) {
        this.selectorPosition = position;
      }

      // Emit will trigger parent to update activeItemId
      // which will call updateSelectorPosition with smooth transition
      this.itemClick.emit(nearestItemId);
    }
  }

  onItemClick(itemId: string) {
    if (this.isDragging) return;

    const item = this.sortedItems.find(i => i.id === itemId);
    if (item && !item.disabled) {
      // Force recentrer le sélecteur sur l'item cliqué (même si c'est déjà actif)
      const position = this.itemPositions.get(itemId);
      if (position !== undefined) {
        this.selectorPosition = position;
      }

      this.itemClick.emit(itemId);
      this.triggerHaptic('light');
    }
  }

  isItemActive(itemId: string): boolean {
    return this.activeItemId === itemId;
  }

  isItemDisabled(itemId: string): boolean {
    return this.sortedItems.find(i => i.id === itemId)?.disabled || false;
  }

  private getItemAtPosition(clientX: number): BubbleItem | null {
    if (!this.bubbleContainer) return null;

    const containerRect = this.bubbleContainer.nativeElement.getBoundingClientRect();
    const relativeX = clientX - containerRect.left;

    // Find item at this position
    for (const item of this.sortedItems) {
      if (item.disabled) continue;

      const itemPos = this.itemPositions.get(item.id);
      if (itemPos !== undefined) {
        const itemLeft = itemPos;
        const itemRight = itemPos + this.BUBBLE_WIDTH;

        if (relativeX >= itemLeft && relativeX <= itemRight) {
          return item;
        }
      }
    }

    return null;
  }

  private triggerHaptic(style: 'light' | 'medium' | 'heavy' = 'light') {
    if (!this.enableHaptics) return;

    if ('vibrate' in navigator) {
      const duration = style === 'light' ? 10 : style === 'medium' ? 20 : 30;
      navigator.vibrate(duration);
    }
  }
}
